// src/adminDashboard/shared/components/DataTable/ColumnVisibilityMenu.tsx
// Dropdown menu for toggling column visibility

import { Columns, Check } from 'lucide-react';
import React, { useState, useRef, useEffect } from 'react';

import { ColumnDef } from './utils/exportHelpers';

interface ColumnVisibilityMenuProps {
    columns: ColumnDef[];
    hiddenColumns?: string[];
    onToggle: (key: string) => void;
}

const ColumnVisibilityMenu: React.FC<ColumnVisibilityMenuProps> = ({
    columns,
    hiddenColumns = [],
    onToggle
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const toggleableColumns = columns.filter(col => col.toggleable !== false);
    const visibleCount = toggleableColumns.filter(col => !hiddenColumns.includes(col.key)).length;

    const handleShowAll = () => {
        toggleableColumns.forEach(col => {
            if (hiddenColumns.includes(col.key)) {
                onToggle(col.key);
            }
        });
    };

    const handleHideAll = () => {
        toggleableColumns.forEach(col => {
            if (!hiddenColumns.includes(col.key)) {
                onToggle(col.key);
            }
        });
    };

    return (
        <div className="relative" ref={menuRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
                <Columns size={16} />
                Columns
                <span className="text-xs text-slate-400">
                    ({visibleCount}/{toggleableColumns.length})
                </span>
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                    {/* Header */}
                    <div className="px-3 py-2 border-b border-slate-100">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-500 uppercase">
                                Toggle Columns
                            </span>
                            <div className="flex gap-1">
                                <button
                                    onClick={handleShowAll}
                                    className="text-xs text-primary hover:text-primary-dark font-medium"
                                >
                                    Show All
                                </button>
                                <span className="text-slate-300">|</span>
                                <button
                                    onClick={handleHideAll}
                                    className="text-xs text-primary hover:text-primary-dark font-medium"
                                >
                                    Hide All
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Column list */}
                    <div className="max-h-80 overflow-y-auto">
                        {toggleableColumns.map((column) => {
                            const isVisible = !hiddenColumns.includes(column.key);
                            return (
                                <button
                                    key={column.key}
                                    onClick={() => onToggle(column.key)}
                                    className="w-full px-3 py-2 flex items-center justify-between hover:bg-slate-50 transition-colors"
                                >
                                    <span className="text-sm text-slate-700">
                                        {column.header}
                                    </span>
                                    {isVisible && (
                                        <Check size={16} className="text-primary" />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ColumnVisibilityMenu;
